import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface SubmitWithReasonsProps {
  /** Human-readable list of what is still missing. Empty = form is valid. */
  reasons: string[];
  /** Disabled for reasons other than validation (pending, no changes, ...). */
  disabled?: boolean;
  /** Show the spinner. */
  pending?: boolean;
  /** Button label. */
  children: React.ReactNode;
}

/**
 * Shared submit button for the order create/edit dialogs.
 *
 * A single place that decides: an incomplete form can never be submitted, and
 * the reason it can't is one hover away instead of a wall of red text.
 */
export function SubmitWithReasons({
  reasons,
  disabled,
  pending,
  children,
}: SubmitWithReasonsProps) {
  const blocked = reasons.length > 0;
  const button = (
    <Button type="submit" disabled={!!disabled || blocked || !!pending}>
      {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {children}
    </Button>
  );

  if (!blocked) return button;

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        {/* A disabled button emits no pointer events, so the trigger wraps it. */}
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-not-allowed" tabIndex={0}>
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" align="end" className="max-w-xs">
          <p className="font-medium">Complete these to continue:</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            {reasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
